import { inject, Injectable } from '@angular/core';
import { TradeKey } from '../model/api';
import { MarketId, Markets } from '../model/common';
import { BinanceWebsocketClient, BinanceWebsocketClientOptions, BybitWebsocketClient, BybitWebsocketClientOptions, OkxWebsocketClient, OkxWebsocketClientOptions } from '.';
import { DefaultLogger as BinanceDefaultLogger } from 'libs/binance-api/src';
import { DefaultLogger as BybitDefaultLogger } from 'libs/bybit-api/src';
import { DefaultLogger as OkxDefaultLogger } from 'libs/okx-api/src';
import { BybitTimeService, BinanceUsdmTimeService, OkxTimeService } from './market-time.service';
import { first } from 'rxjs';
import { MarketLoggerService } from './market-logger.service';

type AnyClient = BybitWebsocketClient | BinanceWebsocketClient | OkxWebsocketClient

@Injectable({
  providedIn: 'root'
})
export class WebsocketClientFactory {

  private bybitTime = inject(BybitTimeService)
  private binanceUsdmTime = inject(BinanceUsdmTimeService)
  private okxTime = inject(OkxTimeService)
  private marketLogger = inject(MarketLoggerService)
  private cache = new Map<string, BybitWebsocketClient | BinanceWebsocketClient | OkxWebsocketClient>()

  createForKey(key: TradeKey) {
    switch (key.market) {
      case Markets.binance: return new WebsocketClientWrapper(this.binance(key))
      case Markets.bybit: return new WebsocketClientWrapper(this.bybit(key))
      case Markets.okx: return new WebsocketClientWrapper(this.okx(key))
    }
    throw new Error(`Unknown ${key.market}`)
  }

  createForMarket(market: MarketId) {
    switch (market) {
      case Markets.binance: return new WebsocketClientWrapper(this.binance())
      case Markets.bybit: return new WebsocketClientWrapper(this.bybit())
      case Markets.okx: return new WebsocketClientWrapper(this.okx())
    }
    throw new Error(`Unknown ${market}`)
  }

  bybit(key?: TradeKey) {
    const cacheKey = 'bybit' + (key ? key.id : '')
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)! as BybitWebsocketClient
    }

    const options: BybitWebsocketClientOptions = {
      requestOptions: {
        headers: {
          'x-referer': 'cscalp',
        }
      }
    }
    if (key) {
      options.key = key.authData.publicKey
      options.secret = key.authData.secretKey
    }

    const customLogger = {
      ...BybitDefaultLogger,
      info:  () => {return},//info: (...params: any[]) => console.log('info', ...params),
      trace: () => {return},//trace: (...params: any[]) => console.log('trace', ...params),
    }

    const client = new BybitWebsocketClient(options, customLogger, this.marketLogger)
    this.bybitTime.offset$.pipe(first()).subscribe(offset => client.setTimeOffsetMs(offset))

    this.cache.set(cacheKey, client)

    return client
  }

  binance(key?: TradeKey) {
    const cacheKey = 'binance' + (key ? key.id : '')
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)! as BinanceWebsocketClient
    }

    const options: BinanceWebsocketClientOptions = {
      disableHeartbeat: true,
      beautify: true,
      restOptions: {
        recvWindow: 5000,
        disableTimeSync: false,
      }
    }
    if (key) {
      options.api_key = key.authData.publicKey
      options.api_secret = key.authData.secretKey
    }

    const customLogger = {
      ...BinanceDefaultLogger,
      info:  () => {return},//info: (...params: any[]) => console.log('info', ...params),
      trace: () => {return},//trace: (...params: any[]) => console.log('trace', ...params),
    }

    const client = new BinanceWebsocketClient(options, customLogger, this.marketLogger)

    this.cache.set(cacheKey, client)

    return client
  }

  okx(key?: TradeKey) {
    const cacheKey = 'okx' + (key ? key.id : '')
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)! as OkxWebsocketClient
    }

    const options: OkxWebsocketClientOptions = {}
    if (key) {
      options.accounts = [{
        apiKey: key.authData.publicKey,
        apiSecret: key.authData.secretKey,
        apiPass: key.authData.password || ''
      }]
    }

    const customLogger = {
      ...OkxDefaultLogger,
      info:  () => {return},//info: (...params: any[]) => console.log('info', ...params),
      trace: () => {return},//trace: (...params: any[]) => console.log('trace', ...params),
      debug: () => {return},//debug: (...params: any[]) => console.log('debug', ...params),
    }

    const client = new OkxWebsocketClient(options, customLogger, this.marketLogger)

    this.cache.set(cacheKey, client)

    return client
  }

}


export class WebsocketClientWrapper {

  constructor(readonly client: AnyClient) {}

  get isBybit() { return this.client instanceof BybitWebsocketClient }
  get isBinance() { return this.client instanceof BinanceWebsocketClient }
  get isOkx() { return this.client instanceof OkxWebsocketClient }

  get bybit(): BybitWebsocketClient {
    if (this.client instanceof BybitWebsocketClient) {
      return this.client
    }
    throw new Error('WsClient type error')
  }

  get binance(): BinanceWebsocketClient {
    if (this.client instanceof BinanceWebsocketClient) {
      return this.client
    }
    throw new Error('WsClient type error')
  }

  get okx(): OkxWebsocketClient {
    if (this.client instanceof OkxWebsocketClient) {
      return this.client
    }
    throw new Error('WsClient type error')
  }

}
